import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  Print,
  LocalShipping,
} from "@mui/icons-material";
import itemIssueService from "../../services/itemIssueService";
import { useSnackbar } from "notistack";

export const ItemIssueDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [itemIssue, setItemIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: "",
    itemIssue: null,
  });
  const [actionNotes, setActionNotes] = useState("");

  useEffect(() => {
    fetchItemIssue();
  }, [id]);

  const fetchItemIssue = async () => {
    try {
      const response = await itemIssueService.getItemIssue(id);
      setItemIssue(response.data.data.itemIssue || response.data.data);
    } catch (error) {
      enqueueSnackbar("Error fetching item issue details", {
        variant: "error",
      });
      navigate("/hospital/hospital-inventory/item-issues");
    } finally {
      setLoading(false);
    }
  };

  const handlePrintIssue = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Item Issue #${itemIssue.issueNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .details { margin: 20px 0; }
            .table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .table th { background-color: #f5f5f5; }
            .totals { float: right; margin-top: 20px; }
            .footer { margin-top: 50px; border-top: 1px solid #333; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ITEM ISSUE</h1>
            <h2>#${itemIssue.issueNumber}</h2>
          </div>
          
          <div class="details">
            <p><strong>Date:</strong> ${new Date(
              itemIssue.issueDate
            ).toLocaleDateString()}</p>
            ${
              itemIssue.department
                ? `<p><strong>Department:</strong> ${itemIssue.department.departmentName} (${itemIssue.department.departmentCode})</p>`
                : ""
            }
            ${
              itemIssue.purpose
                ? `<p><strong>Purpose:</strong> ${itemIssue.purpose}</p>`
                : ""
            }
            ${
              itemIssue.requestedBy
                ? `<p><strong>Requested By:</strong> ${
                    itemIssue.requestedBy.name || itemIssue.requestedBy.email
                  }</p>`
                : ""
            }
            <p><strong>Status:</strong> ${itemIssue.status}</p>
          </div>
          
          <table class="table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Batch</th>
                <th>Qty</th>
                <th>Cost Price</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${itemIssue.items
                .map(
                  (item) => `
                <tr>
                  <td>${item.item.name}</td>
                  <td>${item.batchNumber}</td>
                  <td>${item.quantity}</td>
                  <td>₹${item.costPrice}</td>
                  <td>₹${(item.costPrice * item.quantity).toFixed(2)}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          
          <div class="totals">
            <p><strong>Subtotal: ₹${itemIssue.subtotal.toFixed(2)}</strong></p>
            <p><strong>Tax: ₹${itemIssue.taxAmount.toFixed(2)}</strong></p>
            <p><strong>Total: ₹${itemIssue.grandTotal.toFixed(2)}</strong></p>
          </div>
          
          <div class="footer">
            <p>Authorized Signature: _________________</p>
            <p>Received By: _________________</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleApprove = () => {
    setActionNotes("");
    setActionDialog({
      open: true,
      type: "approve",
      itemIssue: itemIssue,
    });
  };

  const handleConfirmApprove = async () => {
    try {
      await itemIssueService.approveItemIssue(itemIssue._id, {
        notes: actionNotes,
      });

      enqueueSnackbar("Item issue approved successfully", {
        variant: "success",
      });
      fetchItemIssue(); // Refresh data
      setActionDialog({ open: false, type: "", itemIssue: null });
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error approving item issue",
        { variant: "error" }
      );
    }
  };

  const handleSubmit = async () => {
    try {
      await itemIssueService.submitItemIssue(itemIssue._id);
      enqueueSnackbar("Item issue submitted for approval", {
        variant: "success",
      });
      fetchItemIssue(); // Refresh data
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error submitting item issue",
        { variant: "error" }
      );
    }
  };

  const handleIssue = () => {
    setActionNotes("");
    setActionDialog({
      open: true,
      type: "issue",
      itemIssue: itemIssue,
    });
  };

  const handleConfirmIssue = async () => {
    try {
      await itemIssueService.issueItems(itemIssue._id, {
        notes: actionNotes,
      });

      enqueueSnackbar("Items issued successfully", { variant: "success" });
      fetchItemIssue(); // Refresh data
      setActionDialog({ open: false, type: "", itemIssue: null });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || "Error issuing items", {
        variant: "error",
      });
    }
  };

  const handleCancelIssue = () => {
    setActionNotes("");
    setActionDialog({
      open: true,
      type: "cancel",
      itemIssue: itemIssue,
    });
  };

  const handleConfirmCancel = async () => {
    try {
      await itemIssueService.cancelItemIssue(itemIssue._id, {
        reason: actionNotes,
      });
      enqueueSnackbar("Item issue cancelled successfully", {
        variant: "success",
      });
      navigate("/hospital/hospital-inventory/item-issues");
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error cancelling item issue",
        { variant: "error" }
      );
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "issued":
        return "success";
      case "approved":
        return "info";
      case "pending_approval":
        return "warning";
      case "draft":
        return "default";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!itemIssue) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Item issue not found
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/hospital/hospital-inventory/item-issues")}
        >
          Back to Item Issues
        </Button>
        <Typography variant="h4" fontWeight="bold">
          Issue #{itemIssue.issueNumber}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {itemIssue.status === "draft" && (
            <Button
              startIcon={<CheckCircle />}
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Submit for Approval
            </Button>
          )}

          {itemIssue.status === "pending_approval" && (
            <Button
              startIcon={<CheckCircle />}
              variant="contained"
              color="success"
              onClick={handleApprove}
            >
              Approve
            </Button>
          )}
          {itemIssue.status === "approved" && (
            <Button
              startIcon={<LocalShipping />}
              variant="contained"
              color="primary"
              onClick={handleIssue}
            >
              Mark as Issued
            </Button>
          )}
          {(itemIssue.status === "draft" ||
            itemIssue.status === "pending_approval" ||
            itemIssue.status === "approved") && (
            <Button
              startIcon={<Cancel />}
              variant="outlined"
              color="error"
              onClick={handleCancelIssue}
            >
              Cancel
            </Button>
          )}
          <Button
            startIcon={<Print />}
            variant="contained"
            onClick={handlePrintIssue}
          >
            Print
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Items
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Batch</TableCell>
                      <TableCell align="center">Qty</TableCell>
                      <TableCell align="right">Cost Price</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemIssue.items.map((item, index) => {
                      const itemAmount = item.costPrice * item.quantity;

                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="subtitle2">
                              {item.item.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.item.brand} - {item.item.itemType}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {item.batchNumber}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Exp:{" "}
                              {new Date(item.expiryDate).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="right">
                            ₹{item.costPrice.toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            ₹{itemAmount.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {itemIssue.notes && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {itemIssue.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ position: "sticky", top: 100 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Summary
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={itemIssue.status.replace(/_/g, " ").toUpperCase()}
                  color={getStatusColor(itemIssue.status)}
                  sx={{ mb: 1 }}
                />
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  Created: {new Date(itemIssue.createdAt).toLocaleString()}
                </Typography>
                {itemIssue.updatedAt !== itemIssue.createdAt && (
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    Modified: {new Date(itemIssue.updatedAt).toLocaleString()}
                  </Typography>
                )}
              </Box>

              {itemIssue.department && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Department
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {itemIssue.department.departmentName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Code: {itemIssue.department.departmentCode}
                  </Typography>
                  {itemIssue.department.departmentType && (
                    <Typography variant="body2" color="text.secondary">
                      Type: {itemIssue.department.departmentType}
                    </Typography>
                  )}
                  {itemIssue.department.contactPhone && (
                    <Typography variant="body2" color="text.secondary">
                      {itemIssue.department.contactPhone}
                    </Typography>
                  )}
                </Box>
              )}

              {itemIssue.purpose && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Purpose
                  </Typography>
                  <Chip
                    label={itemIssue.purpose.replace(/_/g, " ").toUpperCase()}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              )}

              {itemIssue.requestedBy && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Requested By
                  </Typography>
                  <Typography variant="body1">
                    {itemIssue.requestedBy.name || itemIssue.requestedBy.email}
                  </Typography>
                </Box>
              )}

              {itemIssue.approvedBy && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Approved By
                  </Typography>
                  <Typography variant="body1">
                    {itemIssue.approvedBy.name || itemIssue.approvedBy.email}
                  </Typography>
                  {itemIssue.approvedAt && (
                    <Typography variant="caption" color="text.secondary">
                      {new Date(itemIssue.approvedAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              )}

              {itemIssue.createdBy && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Created By
                  </Typography>
                  <Typography variant="body1">
                    {itemIssue.createdBy.name || itemIssue.createdBy.email}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography>Subtotal:</Typography>
                  <Typography>₹{itemIssue.subtotal.toFixed(2)}</Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography>Tax:</Typography>
                  <Typography>₹{itemIssue.taxAmount.toFixed(2)}</Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography>Total Cost:</Typography>
                  <Typography>
                    ₹{itemIssue.totalCost?.toFixed(2) || "0.00"}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6">Grand Total:</Typography>
                  <Typography variant="h6">
                    ₹{itemIssue.grandTotal.toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {itemIssue.version !== undefined && (
                <Box sx={{ mb: 1 }}>
                  <Chip
                    label={`Version ${itemIssue.version}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Approval Dialog */}
      <Dialog
        open={actionDialog.open && actionDialog.type === "approve"}
        onClose={() =>
          setActionDialog({ open: false, type: "", itemIssue: null })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve Item Issue</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Approving this item issue will allocate stock from the warehouse and
            create department stock records.
          </Alert>
          <Typography variant="body2" gutterBottom>
            Issue #: {actionDialog.itemIssue?.issueNumber}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Department: {actionDialog.itemIssue?.department?.departmentName}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Total Items: {actionDialog.itemIssue?.items?.length}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Approval Notes (Optional)"
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setActionDialog({ open: false, type: "", itemIssue: null })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmApprove}
          >
            Approve Issue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Issue Dialog */}
      <Dialog
        open={actionDialog.open && actionDialog.type === "issue"}
        onClose={() =>
          setActionDialog({ open: false, type: "", itemIssue: null })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark as Issued</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This confirms that the items have been physically distributed to the
            department.
          </Alert>
          <Typography variant="body2" gutterBottom>
            Issue #: {actionDialog.itemIssue?.issueNumber}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Department: {actionDialog.itemIssue?.department?.departmentName}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Issue Notes (Optional)"
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setActionDialog({ open: false, type: "", itemIssue: null })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmIssue}
          >
            Confirm Issue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog
        open={actionDialog.open && actionDialog.type === "cancel"}
        onClose={() =>
          setActionDialog({ open: false, type: "", itemIssue: null })
        }
      >
        <DialogTitle>Cancel Item Issue</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. Stock will be restored to the
            warehouse.
          </Alert>
          <Typography gutterBottom>
            Are you sure you want to cancel issue #
            {actionDialog.itemIssue?.issueNumber}?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Cancellation Reason (Required)"
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setActionDialog({ open: false, type: "", itemIssue: null })
            }
          >
            No, Keep It
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancel}
            disabled={!actionNotes.trim()}
          >
            Yes, Cancel Issue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
